import { Link, useLocation } from "react-router-dom";
import {
  BadgeCheck,
  FolderKanban,
  LayoutGrid,
  LogOut,
  Menu,
} from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import { cn } from "@/lib/utils";
import {
  mobileDrawerWidthCss,
  SHELL_OVERFLOW_CLASS,
} from "@/lib/nav";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ROUTES } from "@/routes";

export function BrandMark({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        "brand-mark flex h-9 w-9 items-center justify-center rounded-xl bg-primary font-display text-[11px] font-bold tracking-tight text-on-primary",
        className,
      )}
      aria-hidden
    >
      tb
    </div>
  );
}

export function ConsoleLogo({
  to = ROUTES.projects,
  compact,
}: {
  to?: string;
  compact?: boolean;
}) {
  return (
    <Link
      to={to}
      className="console-logo group flex items-center gap-3 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary focus-visible:ring-offset-2"
      aria-label={compact ? "TinyBase Console" : undefined}
    >
      <BrandMark />
      {!compact ? (
        <div className="min-w-0">
          <div className="console-logo-name font-display text-sm font-semibold tracking-tight text-text-primary">
            TinyBase
          </div>
          <div className="console-logo-subtitle font-mono text-[10px] uppercase tracking-label text-text-muted">
            console
          </div>
        </div>
      ) : null}
    </Link>
  );
}

export function Statusline({
  items,
  className,
  /** When set, only the first N items render below the platform breakpoint. */
  mobileMaxItems = 2,
}: {
  items: Array<{ label?: string; value: React.ReactNode; live?: boolean }>;
  className?: string;
  mobileMaxItems?: number;
}) {
  return (
    <div
      className={cn("statusline", className)}
      role="status"
      data-mobile-max={mobileMaxItems}
    >
      {items.map((item, i) => (
        <span
          key={i}
          className={cn(
            "statusline-item inline-flex items-center gap-1.5",
            i >= mobileMaxItems ? "max-md:hidden" : null,
          )}
        >
          {i > 0 ? (
            <span className="statusline-sep" aria-hidden>
              ·
            </span>
          ) : null}
          {item.live ? <span className="status-dot" aria-hidden /> : null}
          {item.label ? (
            <span className="text-text-disabled">{item.label}</span>
          ) : null}
          <span className="text-text-secondary">{item.value}</span>
        </span>
      ))}
    </div>
  );
}

export function UserMenu({
  email,
  onSignOut,
}: {
  email: string;
  onSignOut: () => void;
  /** @deprecated Dark chrome removed; kept for call-site compatibility. */
  dark?: boolean;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="user-menu-trigger max-w-[14rem] gap-2 font-normal text-text-muted"
        >
          <span className="user-avatar flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-secondary-fixed text-[10px] font-semibold uppercase text-secondary">
            {email.slice(0, 1)}
          </span>
          <span className="hidden truncate sm:inline">{email}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="font-normal">
          <div className="text-xs text-text-disabled">Signed in as</div>
          <div className="truncate text-sm font-medium text-text-primary">
            {email}
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={onSignOut}
          className="text-error focus:text-error"
        >
          <LogOut className="h-4 w-4" />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function PlatformNavLinks({
  inOperatorArea,
  operatorRole,
  onNavigate,
  compact,
}: {
  inOperatorArea: boolean;
  operatorRole?: string | null;
  onNavigate?: () => void;
  /** Hide section description (mobile drawer density). */
  compact?: boolean;
}) {
  return (
    <nav aria-label="Platform navigation" className="navigation-menu">
      <div className="navigation-section-heading">
        <div className="nav-section-label">Workspace</div>
        {!compact ? (
          <p className="nav-section-description">
            Manage projects and platform operations.
          </p>
        ) : null}
      </div>
      <ul className="navigation-list">
        <li>
          <Link
            to={ROUTES.projects}
            onClick={onNavigate}
            className={inOperatorArea ? "nav-link" : "nav-link-active"}
            aria-current={inOperatorArea ? undefined : "page"}
          >
            <span className="nav-link-icon">
              <FolderKanban
                className="h-4 w-4"
                aria-hidden
                strokeWidth={1.75}
              />
            </span>
            <span className="min-w-0 flex-1">Projects</span>
          </Link>
        </li>
        {operatorRole ? (
          <li>
            <Link
              to={ROUTES.operatorVerifications}
              onClick={onNavigate}
              className={inOperatorArea ? "nav-link-active" : "nav-link"}
              aria-current={inOperatorArea ? "page" : undefined}
            >
              <span className="nav-link-icon">
                <BadgeCheck
                  className="h-4 w-4"
                  aria-hidden
                  strokeWidth={1.75}
                />
              </span>
              <span className="min-w-0 flex-1">Verification reviews</span>
            </Link>
          </li>
        ) : null}
      </ul>
    </nav>
  );
}

/** Platform-level shell (projects list, etc.) — DESIGN.md light documentation chrome. */
export function PlatformShell({
  email,
  onSignOut,
  children,
  title = "Workbench",
  subtitle = "Platform",
  operatorRole,
}: {
  email: string;
  onSignOut: () => void;
  children: React.ReactNode;
  title?: string;
  subtitle?: string;
  operatorRole?: string | null;
}) {
  const location = useLocation();
  const inOperatorArea = location.pathname.includes("/operator/");
  return (
    <TooltipProvider delayDuration={400}>
      <div className={cn("shell-root", SHELL_OVERFLOW_CLASS)}>
        <aside className="navigation-drawer hidden md:sticky md:top-0 md:flex">
          <div className="navigation-drawer-brand">
            <ConsoleLogo />
          </div>
          <Separator />
          <div className="navigation-drawer-scroll overflow-y-auto">
            <PlatformNavLinks
              inOperatorArea={inOperatorArea}
              operatorRole={operatorRole}
            />
          </div>
          <div className="navigation-drawer-footer">
            <div className="mb-3 flex items-center gap-3 px-2">
              <span className="nav-link-icon bg-surface-container-lowest text-secondary">
                <LayoutGrid
                  className="h-4 w-4"
                  aria-hidden
                  strokeWidth={1.75}
                />
              </span>
              <div className="min-w-0">
                <div className="text-[11px] font-bold uppercase tracking-label text-text-muted">
                  {operatorRole ? "Operator" : "Platform user"}
                </div>
                <div className="mt-1 truncate text-xs text-text-primary">
                  {email}
                </div>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="mt-2 w-full justify-start"
              onClick={onSignOut}
            >
              <LogOut className="h-4 w-4" strokeWidth={1.75} />
              Sign out
            </Button>
          </div>
        </aside>

        <div className="shell-main">
          <header className="shell-header sticky top-0 z-header border-b">
            <div className="shell-header-bar flex h-header items-center justify-between gap-3 px-4 sm:px-6 lg:px-8">
              <div className="flex min-w-0 items-center gap-2.5">
                <div className="md:hidden">
                  <MobileNavSheet
                    title="Navigation"
                    description="TinyBase Console"
                    triggerClassName="md:hidden"
                    footer={(close) => (
                      <div className="space-y-3">
                        <div className="flex items-center gap-3 px-1">
                          <span className="nav-link-icon bg-surface-container text-secondary">
                            <LayoutGrid
                              className="h-4 w-4"
                              aria-hidden
                              strokeWidth={1.75}
                            />
                          </span>
                          <div className="min-w-0">
                            <div className="text-[11px] font-bold uppercase tracking-label text-text-muted">
                              {operatorRole ? "Operator" : "Platform user"}
                            </div>
                            <div className="mt-0.5 truncate text-xs text-text-primary">
                              {email}
                            </div>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="w-full justify-start"
                          onClick={() => {
                            close();
                            onSignOut();
                          }}
                        >
                          <LogOut className="h-4 w-4" strokeWidth={1.75} />
                          Sign out
                        </Button>
                      </div>
                    )}
                  >
                    {(close) => (
                      <>
                        <div className="mb-6">
                          <ConsoleLogo to={ROUTES.projects} />
                        </div>
                        <PlatformNavLinks
                          inOperatorArea={inOperatorArea}
                          operatorRole={operatorRole}
                          onNavigate={close}
                          compact
                        />
                      </>
                    )}
                  </MobileNavSheet>
                </div>
                <BrandMark className="md:hidden" />
                <div className="min-w-0">
                  <div className="eyebrow">{subtitle}</div>
                  <div className="truncate font-sans text-sm font-semibold text-text-primary">
                    {title}
                  </div>
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <UserMenu email={email} onSignOut={onSignOut} />
              </div>
            </div>
            <Statusline
              mobileMaxItems={2}
              items={[
                { label: "env", value: "local", live: true },
                { label: "surface", value: "platform" },
                { label: "session", value: email.split("@")[0] },
              ]}
            />
          </header>
          <main className="shell-content max-w-6xl">{children}</main>
        </div>
      </div>
    </TooltipProvider>
  );
}

type MobileNavRender = ReactNode | ((close: () => void) => ReactNode);

export function MobileNavSheet({
  title,
  description,
  children,
  footer,
  triggerClassName = "lg:hidden",
}: {
  title: string;
  description?: string;
  children: MobileNavRender;
  footer?: MobileNavRender;
  /** Tailwind visibility for the menu trigger (default: project shell lg). */
  triggerClassName?: string;
}) {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.search]);

  const close = () => setOpen(false);
  const body = typeof children === "function" ? children(close) : children;
  const foot = typeof footer === "function" ? footer(close) : footer;

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button
          variant="outline"
          size="icon"
          className={cn(
            "mobile-nav-trigger border-transparent bg-transparent hover:border-border-standard hover:bg-background-subtle",
            triggerClassName,
          )}
          aria-label="Open navigation"
          aria-expanded={open}
          aria-controls="mobile-navigation-panel"
        >
          <Menu className="h-5 w-5" strokeWidth={1.75} />
        </Button>
      </SheetTrigger>
      <SheetContent
        side="left"
        id="mobile-navigation-panel"
        className="mobile-nav-sheet flex flex-col gap-0 overflow-hidden border-border-standard bg-surface-container-lowest p-0 text-text-primary"
        style={{ width: mobileDrawerWidthCss() }}
      >
        <SheetHeader className="mobile-nav-sheet-header shrink-0 border-b border-border-standard px-4 py-4 pr-14 text-left">
          <SheetTitle className="text-sm">{title}</SheetTitle>
          {description ? (
            <SheetDescription className="text-xs">
              {description}
            </SheetDescription>
          ) : null}
        </SheetHeader>
        <div className="mobile-nav-sheet-body min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-5">
          {body}
        </div>
        {foot ? (
          <div className="mobile-nav-sheet-footer shrink-0 border-t border-border-standard px-4 py-4">
            {foot}
          </div>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}
